import * as THREE from "three"

/**
 * A stylized sounding-rocket mesh for telemetry visualization.
 * Body + nose cone + 4 fins + engine nozzle + exhaust flame.
 */
export function buildRocket(color: number = 0x3b82f6): THREE.Group {
  const group = new THREE.Group()

  // Fuselage
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.45, 0.45, 4.2, 16),
    new THREE.MeshStandardMaterial({ color, roughness: 0.35, metalness: 0.25 }),
  )
  body.rotation.x = Math.PI / 2
  body.castShadow = true
  group.add(body)

  // Nose cone
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.45, 1.9, 16),
    new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.3, metalness: 0.8 }),
  )
  nose.position.set(0, 2.3, 0)
  nose.rotation.x = Math.PI / 2
  nose.castShadow = true
  group.add(nose)

  // Fins (cross configuration)
  const finMaterial = new THREE.MeshStandardMaterial({
    color: 0x0f172a,
    roughness: 0.6,
    metalness: 0.1,
  })
  const finGeo = new THREE.BoxGeometry(0.1, 1.1, 0.55)
  const finPositions = [
    [0.7, 0, 0],
    [-0.7, 0, 0],
    [0, 0, 0.7],
    [0, 0, -0.7],
  ]
  finPositions.forEach(([x, , z]) => {
    const fin = new THREE.Mesh(finGeo, finMaterial)
    fin.position.set(x, -1.35, z)
    fin.rotation.x = Math.PI / 2
    fin.castShadow = true
    group.add(fin)
  })

  // Nozzle
  const nozzle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.42, 0.5, 0.9, 14),
    new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.4, metalness: 0.6 }),
  )
  nozzle.position.set(0, -2.5, 0)
  nozzle.rotation.x = Math.PI / 2
  nozzle.castShadow = true
  group.add(nozzle)

  // Exhaust flame (off by default)
  const flame = new THREE.Mesh(
    new THREE.ConeGeometry(0.28, 1.1, 12),
    new THREE.MeshBasicMaterial({ color: 0xfbbf24, transparent: true, opacity: 0.9 }),
  )
  flame.position.set(0, -3.55, 0)
  flame.rotation.x = Math.PI / 2
  flame.scale.set(1, 0.05, 1)
  flame.visible = false
  group.add(flame)

  // Flame inner core
  const core = new THREE.Mesh(
    new THREE.ConeGeometry(0.14, 0.8, 10),
    new THREE.MeshBasicMaterial({ color: "#ffffff" }),
  )
  core.position.set(0, -3.55, 0)
  core.rotation.x = Math.PI / 2
  core.scale.set(1, 0.05, 1)
  core.visible = false
  group.add(core)

  ;(group as any).flameMesh = flame
  ;(group as any).flameCore = core

  return group
}

export function setRocketState(
  rocket: THREE.Group,
  { x, z, y, pitch = 0, yaw = 0, roll = 0, throttle = 0 }: { x: number; z: number; y: number; pitch?: number; yaw?: number; roll?: number; throttle?: number },
) {
  rocket.position.set(x, y, z)
  // pitch around X (local), yaw around Y, roll around Z
  rocket.rotation.set(pitch, yaw, roll, "YXZ")

  const flame = (rocket as any).flameMesh as THREE.Mesh | undefined
  const core = (rocket as any).flameCore as THREE.Mesh | undefined
  if (flame && core) {
    const active = throttle > 0.05
    flame.visible = active
    core.visible = active
    const s = Math.max(0.1, Math.min(1, throttle * 1.4))
    flame.scale.set(1, s, 1)
    core.scale.set(1, s * 0.85, 1)
  }
}
